import type { Vec2 } from '../../geo/types';

export interface PigeonPose {
  readonly x: number;
  readonly y: number;
  readonly lift: number;
  readonly heading: number;
  readonly peck: number;
  readonly wing: number;
  readonly flying: boolean;
  readonly walked: number;
  readonly moving: boolean;
}
export interface PigeonFoot {
  readonly forward: number;
  readonly lift: number;
}

/** A planted foot moves back relative to the advancing body; the other swings forward. */
export function pigeonFootAt(walked: number, side: number, moving: boolean): PigeonFoot {
  if (!moving) return { forward: 0, lift: 0 };
  const phase = (((walked / 0.12 + (side < 0 ? 0.5 : 0)) % 1) + 1) % 1;
  if (phase < 0.5) return { forward: 0.03 - phase * 0.12, lift: 0 };
  const swing = (phase - 0.5) * 2;
  return { forward: -0.03 + swing * 0.06, lift: Math.sin(Math.PI * swing) * 0.035 };
}

/** Walk in short bouts, stop to peck, then return to the feeding patch after a startled flight. */
export function pigeonPoseAt(
  home: Vec2,
  time: number,
  phase: number,
  flightSince: number
): PigeonPose {
  const clock = time + phase,
    cycle = Math.floor(clock / 5.5),
    into = ((clock % 5.5) + 5.5) % 5.5;
  const walked = cycle * 0.45 + Math.min(into, 1.8) * 0.25;
  const moving = into < 1.8;
  const angle = walked / 0.4 + phase;
  const flying = flightSince >= 0 && flightSince < 2.8;
  const arc = flying ? Math.sin((Math.PI * flightSince) / 2.8) : 0;
  return {
    x: home[0] + Math.cos(angle) * 0.4 + arc * Math.cos(phase) * 1.1,
    y: home[1] + Math.sin(angle) * 0.4 + arc * Math.sin(phase) * 1.1,
    lift:
      arc * 1.6 +
      (moving && !flying ? Math.abs(Math.sin((walked / 0.12) * Math.PI * 2)) * 0.004 : 0),
    heading: angle + Math.PI / 2,
    peck: flying || moving ? 0 : Math.max(0, Math.sin(time * 3.8 + phase)) * 0.9,
    wing: flying ? Math.sin(flightSince * 35 + phase) * 0.95 : 0,
    flying,
    walked,
    moving: moving && !flying,
  };
}
